import { PageActionBar } from "components/PageActionBar";
import { useEligibilityActivity } from "pages/CreateOrder/hooks/eligibilityActivity";
import { useOrderReadiness } from "pages/CreateOrder/hooks/useOrderReadiness";
import {
  providerPaymentSetupIncompleteMessage,
  UNLINKED_PAYOUT_PROVIDERS_FIELD,
} from "pages/CreateOrder/utils/payoutProviderSetup";
import { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { SupportEmailMessages } from "../SupportEmailText";

/** The order pages' action bar: the shared sticky bar plus the readiness hint. */
const FILL_REQUIRED_HINT =
  "Please, fill in all required fields to create estimate";

export function StickyFooter({ children }: { children: ReactNode }) {
  // Ready apart from the providers' payment setup; with an unlinked provider the field hint may still apply too.
  const isReadyApartFromPayoutProviders = useOrderReadiness({
    allowUnlinkedPayoutProviders: true,
  });
  // CLE-2971 — `useOrderReadiness` is false while a check is pending; say so, not "required fields".
  const isCheckingEligibility = useEligibilityActivity();
  // CLE-3074 — say which provider's payment setup blocks the estimate, not "required fields". Read
  // through `?.`: every order page mounts this inside its form, but nothing else here needs one.
  const unlinkedPayoutProviders: string[] =
    useFormContext()?.watch(UNLINKED_PAYOUT_PROVIDERS_FIELD) ?? [];
  // The same as useOrderReadiness(): every term but the providers', and no unlinked provider.
  const isReady =
    isReadyApartFromPayoutProviders && unlinkedPayoutProviders.length === 0;

  let hint: ReactNode = FILL_REQUIRED_HINT;
  if (isCheckingEligibility) hint = "Checking eligibility, one moment…";
  else if (unlinkedPayoutProviders.length) {
    hint = (
      <SupportEmailMessages
        messages={[
          ...unlinkedPayoutProviders.map(providerPaymentSetupIncompleteMessage),
          ...(isReadyApartFromPayoutProviders ? [] : [FILL_REQUIRED_HINT]),
        ]}
      />
    );
  } else if (isReady) hint = "Ready to generate and send";

  return (
    <PageActionBar>
      <span className="footer-hint" data-testid="pageActionsHint">
        {hint}
      </span>

      {children}
    </PageActionBar>
  );
}
